// =====================================================
// CLINICAL TEMPLATE BUILDER - Constructor de Plantillas
// Interfaz tipo Google Forms para crear/editar plantillas
// de historias clínicas personalizadas
// =====================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Settings2,
  Type,
  AlignLeft,
  Hash,
  Calendar,
  List,
  CircleDot,
  CheckSquare,
  Paperclip,
  Pen,
  MessageSquare,
  HelpCircle,
  X,
  FileText,
  Palette,
  ClipboardList,
  Stethoscope,
  Baby,
  Heart,
  Brain,
  Bone,
  Activity,
  Pill,
  Syringe,
  Thermometer,
  Shield,
  Users,
  HeartPulse,
  type LucideIcon
} from 'lucide-react';

import {
  ClinicalTemplate,
  TemplateSection,
  TemplateField,
  FIELD_TYPES,
  DEFAULT_TEMPLATE_COLORS,
  DEFAULT_TEMPLATE_ICONS
} from '../../types/clinicalRecords.types';

import clinicalRecordsApi from '../../services/clinicalRecords.api';

// =====================================================
// TIPOS INTERNOS DEL BUILDER
// =====================================================

interface BuilderSection {
  id: string; // ID temporal para el builder
  name: string;
  description: string;
  is_collapsible: boolean;
  is_expanded_default: boolean;
  fields: BuilderField[];
  isCollapsed: boolean; // Estado UI del builder
}

interface BuilderField {
  id: string; // ID temporal para el builder
  field_label: string;
  field_type: string;
  field_key: string;
  placeholder: string;
  default_value: string;
  options: string[];
  is_required: boolean;
  is_visible: boolean;
  help_text: string;
  width: 'full' | 'half' | 'third';
  validation_rules: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
  };
  showAdvanced: boolean; // Estado UI del builder
}

// =====================================================
// HELPERS
// =====================================================

const generateId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const FIELD_TYPE_ICONS: Record<string, LucideIcon> = {
  text: Type,
  textarea: AlignLeft,
  number: Hash,
  date: Calendar,
  select: List,
  radio: CircleDot,
  checkbox: CheckSquare,
  file: Paperclip,
  drawing: Pen,
  textarea_with_attachments: MessageSquare
};

const TEMPLATE_ICON_OPTIONS: { name: string; icon: LucideIcon }[] = [
  { name: 'clipboard', icon: ClipboardList },
  { name: 'stethoscope', icon: Stethoscope },
  { name: 'baby', icon: Baby },
  { name: 'heart', icon: Heart },
  { name: 'brain', icon: Brain },
  { name: 'bone', icon: Bone },
  { name: 'activity', icon: Activity },
  { name: 'pill', icon: Pill },
  { name: 'syringe', icon: Syringe },
  { name: 'thermometer', icon: Thermometer },
  { name: 'shield', icon: Shield },
  { name: 'users', icon: Users },
  { name: 'heart-pulse', icon: HeartPulse },
  { name: 'file-text', icon: FileText },
  { name: 'eye', icon: Eye }
];

const createEmptyField = (): BuilderField => ({
  id: generateId(),
  field_label: '',
  field_type: 'text',
  field_key: '',
  placeholder: '',
  default_value: '',
  options: [],
  is_required: false,
  is_visible: true,
  help_text: '',
  width: 'full',
  validation_rules: {},
  showAdvanced: false
});

const createEmptySection = (): BuilderSection => ({
  id: generateId(),
  name: '',
  description: '',
  is_collapsible: true,
  is_expanded_default: true,
  fields: [createEmptyField()],
  isCollapsed: false
});

// =====================================================
// COMPONENTE: EDITOR DE CAMPO INDIVIDUAL
// =====================================================

interface FieldEditorProps {
  field: BuilderField;
  index: number;
  totalFields: number;
  onChange: (field: BuilderField) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field, index, totalFields, onChange, onRemove, onDuplicate, onMoveUp, onMoveDown
}) => {
  const FieldIcon = FIELD_TYPE_ICONS[field.field_type] || Type;
  const hasOptions = ['select', 'radio', 'checkbox'].includes(field.field_type);

  const updateField = (updates: Partial<BuilderField>) => {
    onChange({ ...field, ...updates });
  };

  const addOption = () => {
    updateField({ options: [...field.options, ''] });
  };

  const updateOption = (idx: number, value: string) => {
    const newOptions = [...field.options];
    newOptions[idx] = value;
    updateField({ options: newOptions });
  };

  const removeOption = (idx: number) => {
    updateField({ options: field.options.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors group">
      {/* Header del campo */}
      <div className="flex items-start gap-3">
        {/* Drag handle */}
        <div className="pt-2 cursor-grab text-gray-300 group-hover:text-gray-400">
          <GripVertical className="w-5 h-5" />
        </div>

        {/* Contenido principal */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Fila: nombre + tipo */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={field.field_label}
                onChange={(e) => updateField({ field_label: e.target.value })}
                placeholder="Nombre del campo (ej: Motivo de consulta)"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
              />
            </div>
            <div className="w-full sm:w-48">
              <select
                value={field.field_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  const updates: Partial<BuilderField> = { field_type: newType };
                  // Si cambia a tipo con opciones y no tiene, agregar 2 vacías
                  if (['select', 'radio', 'checkbox'].includes(newType) && field.options.length === 0) {
                    updates.options = ['', ''];
                  }
                  updateField(updates);
                }}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-gray-50"
              >
                {Object.entries(FIELD_TYPES).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Placeholder */}
          <input
            type="text"
            value={field.placeholder}
            onChange={(e) => updateField({ placeholder: e.target.value })}
            placeholder="Texto de ayuda dentro del campo (opcional)"
            className="w-full px-3 py-2 border border-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-gray-500"
          />

          {/* Opciones para select/radio/checkbox */}
          {hasOptions && (
            <div className="space-y-2 pl-2 border-l-2 border-blue-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Opciones</p>
              {field.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-5 text-right">{idx + 1}.</span>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    placeholder={`Opción ${idx + 1}`}
                    className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(idx)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                    disabled={field.options.length <= 1}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addOption}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 ml-7"
              >
                <Plus className="w-3.5 h-3.5" />
                Agregar opción
              </button>
            </div>
          )}

          {/* Configuración avanzada */}
          {field.showAdvanced && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Ancho */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Ancho</label>
                  <select
                    value={field.width}
                    onChange={(e) => updateField({ width: e.target.value as any })}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="full">Completo</option>
                    <option value="half">Mitad</option>
                    <option value="third">Tercio</option>
                  </select>
                </div>

                {/* Texto de ayuda */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Texto de ayuda</label>
                  <input
                    type="text"
                    value={field.help_text}
                    onChange={(e) => updateField({ help_text: e.target.value })}
                    placeholder="Instrucción para el profesional"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Validaciones numéricas */}
              {field.field_type === 'number' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Valor mínimo</label>
                    <input
                      type="number"
                      value={field.validation_rules.min ?? ''}
                      onChange={(e) => updateField({
                        validation_rules: { ...field.validation_rules, min: e.target.value ? Number(e.target.value) : undefined }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Valor máximo</label>
                    <input
                      type="number"
                      value={field.validation_rules.max ?? ''}
                      onChange={(e) => updateField({
                        validation_rules: { ...field.validation_rules, max: e.target.value ? Number(e.target.value) : undefined }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Barra de acciones del campo */}
          <div className="flex flex-wrap items-center justify-between gap-y-2 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-3">
              {/* Toggle requerido */}
              <label className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => updateField({ is_required: !field.is_required })}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                    field.is_required ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                    field.is_required ? 'translate-x-4' : ''
                  }`} />
                </div>
                <span className="text-xs text-gray-600">Obligatorio</span>
              </label>

              {/* Toggle avanzado */}
              <button
                type="button"
                onClick={() => updateField({ showAdvanced: !field.showAdvanced })}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${
                  field.showAdvanced ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Settings2 className="w-3.5 h-3.5" />
                Avanzado
              </button>
            </div>

            <div className="flex items-center gap-1">
              {/* Mover arriba/abajo */}
              <button
                type="button"
                onClick={onMoveUp}
                disabled={index === 0}
                className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-md hover:bg-gray-100 transition-colors"
                title="Mover arriba"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={index === totalFields - 1}
                className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-md hover:bg-gray-100 transition-colors"
                title="Mover abajo"
              >
                <ChevronDown className="w-4 h-4" />
              </button>

              {/* Duplicar */}
              <button
                type="button"
                onClick={onDuplicate}
                className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                title="Duplicar campo"
              >
                <Copy className="w-4 h-4" />
              </button>

              {/* Eliminar */}
              <button
                type="button"
                onClick={onRemove}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                title="Eliminar campo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// =====================================================
// COMPONENTE: EDITOR DE SECCIÓN
// =====================================================

interface SectionEditorProps {
  section: BuilderSection;
  index: number;
  totalSections: number;
  onChange: (section: BuilderSection) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  section, index, totalSections, onChange, onRemove, onMoveUp, onMoveDown
}) => {
  
  const updateSection = (updates: Partial<BuilderSection>) => {
    onChange({ ...section, ...updates });
  };

  const updateField = (fieldIndex: number, updatedField: BuilderField) => {
    const newFields = [...section.fields];
    newFields[fieldIndex] = updatedField;
    updateSection({ fields: newFields });
  };

  const addField = () => {
    updateSection({ fields: [...section.fields, createEmptyField()] });
  };

  const removeField = (fieldIndex: number) => {
    if (section.fields.length <= 1) return; // Al menos 1 campo
    updateSection({ fields: section.fields.filter((_, i) => i !== fieldIndex) });
  };

  const duplicateField = (fieldIndex: number) => {
    const original = section.fields[fieldIndex];
    const copy: BuilderField = {
      ...original,
      id: generateId(),
      field_label: `${original.field_label} (copia)`,
      field_key: ''
    };
    const newFields = [...section.fields];
    newFields.splice(fieldIndex + 1, 0, copy);
    updateSection({ fields: newFields });
  };

  const moveField = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= section.fields.length) return;
    const newFields = [...section.fields];
    [newFields[fromIndex], newFields[toIndex]] = [newFields[toIndex], newFields[fromIndex]];
    updateSection({ fields: newFields });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header de la sección */}
      <div className="bg-gradient-to-r from-gray-50 to-white p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {/* Drag handle */}
          <div className="cursor-grab text-gray-300 hover:text-gray-400">
            <GripVertical className="w-5 h-5" />
          </div>

          {/* Nombre de sección */}
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={section.name}
              onChange={(e) => updateSection({ name: e.target.value })}
              placeholder={`Nombre de la sección ${index + 1} (ej: Datos Vitales, Examen Físico...)`}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold text-gray-900"
            />
          </div>

          {/* Acciones de sección */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={index === 0}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-md hover:bg-gray-100 transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={index === totalSections - 1}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-md hover:bg-gray-100 transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
            </button>

            {/* Colapsar/expandir */}
            <button
              type="button"
              onClick={() => updateSection({ isCollapsed: !section.isCollapsed })}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
            >
              {section.isCollapsed ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>

            {/* Eliminar sección */}
            {totalSections > 1 && (
              <button
                type="button"
                onClick={onRemove}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                title="Eliminar sección"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Descripción de sección */}
        <div className="mt-2 ml-8">
          <input
            type="text"
            value={section.description}
            onChange={(e) => updateSection({ description: e.target.value })}
            placeholder="Descripción de la sección (opcional)"
            className="w-full px-3 py-1.5 bg-transparent border border-transparent hover:border-gray-200 focus:border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-gray-500"
          />
        </div>
      </div>

      {/* Campos de la sección */}
      {!section.isCollapsed && (
        <div className="p-4 space-y-3">
          {section.fields.map((field, fIdx) => (
            <FieldEditor
              key={field.id}
              field={field}
              index={fIdx}
              totalFields={section.fields.length}
              onChange={(updated) => updateField(fIdx, updated)}
              onRemove={() => removeField(fIdx)}
              onDuplicate={() => duplicateField(fIdx)}
              onMoveUp={() => moveField(fIdx, 'up')}
              onMoveDown={() => moveField(fIdx, 'down')}
            />
          ))}

          {/* Botón agregar campo */}
          <button
            type="button"
            onClick={addField}
            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">Agregar campo</span>
          </button>
        </div>
      )}

      {section.isCollapsed && (
        <div className="p-4 text-center text-sm text-gray-400">
          {section.fields.length} campo{section.fields.length !== 1 ? 's' : ''} — Clic en 
          <Eye className="w-3.5 h-3.5 inline mx-1" /> para expandir
        </div>
      )}
    </div>
  );
};


// =====================================================
// COMPONENTE PRINCIPAL: TEMPLATE BUILDER
// =====================================================

const ClinicalTemplateBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditing = !!templateId;

  // Estados del formulario
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateSpecialty, setTemplateSpecialty] = useState('');
  const [templateIcon, setTemplateIcon] = useState('clipboard');
  const [templateColor, setTemplateColor] = useState('#3B82F6');
  const [sections, setSections] = useState<BuilderSection[]>([createEmptySection()]);
  
  // Estados UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Cargar plantilla si estamos editando
  useEffect(() => {
    if (isEditing && templateId) {
      loadTemplate(parseInt(templateId));
    }
  }, [templateId, isEditing]);

  const loadTemplate = async (id: number) => {
    setLoading(true);
    try {
      const response = await clinicalRecordsApi.getTemplateById(id);
      const t = response.template;
      
      setTemplateName(t.name);
      setTemplateDescription(t.description || '');
      setTemplateSpecialty(t.specialty || '');
      setTemplateIcon(t.icon || 'clipboard');
      setTemplateColor(t.color || '#3B82F6');

      if (t.sections && t.sections.length > 0) {
        setSections(t.sections.map((s: TemplateSection) => ({
          id: `existing_${s.id}`,
          name: s.name,
          description: s.description || '',
          is_collapsible: s.is_collapsible,
          is_expanded_default: s.is_expanded_default,
          isCollapsed: false,
          fields: s.fields.map((f: TemplateField) => ({
            id: `existing_${f.id}`,
            field_label: f.field_label,
            field_type: f.field_type,
            field_key: f.field_key,
            placeholder: f.placeholder || '',
            default_value: f.default_value || '',
            options: Array.isArray(f.options) ? f.options.map((o: any) => typeof o === 'string' ? o : o.value || o.label || '') : [],
            is_required: f.is_required,
            is_visible: f.is_visible,
            help_text: f.help_text || '',
            width: f.width || 'full',
            validation_rules: f.validation_rules || {},
            showAdvanced: false
          }))
        })));
      }
    } catch (err: any) {
      setError('Error al cargar la plantilla');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Validar antes de guardar
  const validate = (): string | null => {
    if (!templateName.trim()) return 'El nombre de la plantilla es obligatorio';
    
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      if (!section.name.trim()) return `La sección ${i + 1} necesita un nombre`;
      
      for (let j = 0; j < section.fields.length; j++) {
        const field = section.fields[j];
        if (!field.field_label.trim()) return `El campo ${j + 1} de "${section.name}" necesita un nombre`;
        
        if (['select', 'radio', 'checkbox'].includes(field.field_type)) {
          const validOptions = field.options.filter(o => o.trim());
          if (validOptions.length < 1) {
            return `El campo "${field.field_label}" necesita al menos una opción`;
          }
        }
      }
    }
    
    return null;
  };

  // Guardar plantilla
  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        specialty: templateSpecialty.trim() || undefined,
        icon: templateIcon,
        color: templateColor,
        sections: sections.map(s => ({
          name: s.name.trim(),
          description: s.description.trim() || undefined,
          is_collapsible: s.is_collapsible,
          is_expanded_default: s.is_expanded_default,
          fields: s.fields.map(f => ({
            field_label: f.field_label.trim(),
            field_type: f.field_type,
            field_key: f.field_key || undefined,
            placeholder: f.placeholder.trim() || undefined,
            default_value: f.default_value || undefined,
            options: ['select', 'radio', 'checkbox'].includes(f.field_type)
              ? f.options.filter(o => o.trim())
              : undefined,
            validation_rules: Object.keys(f.validation_rules).length > 0 
              ? f.validation_rules : undefined,
            is_required: f.is_required,
            is_visible: f.is_visible,
            help_text: f.help_text.trim() || undefined,
            width: f.width
          }))
        }))
      };

      if (isEditing && templateId) {
        await (clinicalRecordsApi as any).updateTemplate(parseInt(templateId), payload);
      } else {
        await (clinicalRecordsApi as any).createTemplate(payload);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/professional/clinical-records');
      }, 1500);

    } catch (err: any) {
      setError(err.message || 'Error al guardar la plantilla');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Funciones de secciones
  const addSection = () => {
    setSections([...sections, createEmptySection()]);
  };

  const updateSection = (index: number, updated: BuilderSection) => {
    const newSections = [...sections];
    newSections[index] = updated;
    setSections(newSections);
  };

  const removeSection = (index: number) => {
    if (sections.length <= 1) return;
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSection = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= sections.length) return;
    const newSections = [...sections];
    [newSections[fromIndex], newSections[toIndex]] = [newSections[toIndex], newSections[fromIndex]];
    setSections(newSections);
  };

  // Contar campos totales
  const totalFields = sections.reduce((sum, s) => sum + s.fields.length, 0);

  // =====================================================
  // RENDER
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <LoadingSpinner size={48} label="Cargando plantilla..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header fijo */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between min-h-[56px] sm:h-16 py-2 sm:py-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                onClick={() => navigate('/professional/clinical-records')}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                  {isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}
                </h1>
                <p className="text-xs text-gray-500">
                  {sections.length} sección{sections.length !== 1 ? 'es' : ''} • {totalFields} campo{totalFields !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate('/professional/clinical-records')}
                className="hidden sm:block px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || success}
                className="flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 sm:px-5 py-2 rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95 disabled:opacity-50 text-sm"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Guardando...</span>
                  </>
                ) : success ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">¡Guardado!</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span className="sm:hidden">Guardar</span>
                    <span className="hidden sm:inline">Guardar Plantilla</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium text-sm">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Configuración de la plantilla */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-blue-500" />
            Información de la Plantilla
          </h2>

          <div className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la plantilla <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Ej: Consulta de Neurología Infantil, Control Epilepsia..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción (opcional)
              </label>
              <textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Breve descripción de para qué se usa esta plantilla..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Especialidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Especialidad</label>
                <input
                  type="text"
                  value={templateSpecialty}
                  onChange={(e) => setTemplateSpecialty(e.target.value)}
                  placeholder="Ej: Neurología Infantil"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Ícono */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ícono</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setShowIconPicker(!showIconPicker); setShowColorPicker(false); }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl flex items-center gap-2 hover:bg-gray-50 text-sm"
                  >
                    {(() => {
                      const iconObj = TEMPLATE_ICON_OPTIONS.find(i => i.name === templateIcon);
                      const IconComp = iconObj?.icon || ClipboardList;
                      return <IconComp className="w-4 h-4" style={{ color: templateColor }} />;
                    })()}
                    <span className="text-gray-600 capitalize">{templateIcon}</span>
                  </button>

                  {showIconPicker && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-50 w-64">
                      <div className="grid grid-cols-5 gap-2">
                        {TEMPLATE_ICON_OPTIONS.map(({ name, icon: IconComp }) => (
                          <button
                            key={name}
                            type="button"
                            onClick={() => { setTemplateIcon(name); setShowIconPicker(false); }}
                            className={`p-2.5 rounded-lg transition-all ${
                              templateIcon === name
                                ? 'bg-blue-100 ring-2 ring-blue-500'
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <IconComp className="w-5 h-5 mx-auto" style={{ color: templateColor }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setShowColorPicker(!showColorPicker); setShowIconPicker(false); }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl flex items-center gap-2 hover:bg-gray-50 text-sm"
                  >
                    <div className="w-5 h-5 rounded-full border border-gray-300" style={{ backgroundColor: templateColor }} />
                    <span className="text-gray-600">{templateColor}</span>
                  </button>

                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-50">
                      <div className="grid grid-cols-4 gap-2">
                        {DEFAULT_TEMPLATE_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => { setTemplateColor(color); setShowColorPicker(false); }}
                            className={`w-10 h-10 rounded-lg transition-all ${
                              templateColor === color ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : 'hover:scale-105'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview de la plantilla */}
        <div
          className="rounded-2xl p-4 flex items-center gap-4"
          style={{ backgroundColor: `${templateColor}10`, borderLeft: `4px solid ${templateColor}` }}
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${templateColor}20`, color: templateColor }}
          >
            {(() => {
              const iconObj = TEMPLATE_ICON_OPTIONS.find(i => i.name === templateIcon);
              const IconComp = iconObj?.icon || ClipboardList;
              return <IconComp className="w-6 h-6" />;
            })()}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{templateName || 'Nombre de la plantilla'}</p>
            <p className="text-sm text-gray-500">
              {templateSpecialty || 'Especialidad'} • {sections.length} sección{sections.length !== 1 ? 'es' : ''} • {totalFields} campo{totalFields !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Secciones */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Secciones del formulario
            </h2>
          </div>

          {sections.map((section, sIdx) => (
            <SectionEditor
              key={section.id}
              section={section}
              index={sIdx}
              totalSections={sections.length}
              onChange={(updated) => updateSection(sIdx, updated)}
              onRemove={() => removeSection(sIdx)}
              onMoveUp={() => moveSection(sIdx, 'up')}
              onMoveDown={() => moveSection(sIdx, 'down')}
            />
          ))}

          {/* Botón agregar sección */}
          <button
            type="button"
            onClick={addSection}
            className="w-full py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/30 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">Agregar Sección</span>
          </button>
        </div>

        {/* Botones finales */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 pb-8">
          <button
            type="button"
            onClick={() => navigate('/professional/clinical-records')}
            className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || success}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Guardando...
              </>
            ) : success ? (
              <>
                <CheckCircle className="w-5 h-5" />
                ¡Plantilla guardada!
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEditing ? 'Actualizar Plantilla' : 'Guardar Plantilla'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicalTemplateBuilder;